import { useState, useCallback, useEffect } from 'react';
import { AutoContentEnhancementService, EnhancementProgress } from '@/services/AutoContentEnhancementService';
import { logger } from '@/utils/logger';
import { supabase } from '@/integrations/supabase/client';

const ENHANCEMENT_COMPLETED_KEY = 'autoEnhancementCompleted'; // Permanent completion flag
const ENHANCEMENT_COOLDOWN_MS = 0; // NO COOLDOWN - Run immediately
const LAST_ENHANCEMENT_KEY = 'lastAutoEnhancement';
const ENHANCEMENT_DISABLED_KEY = 'autoEnhancementDisabled';
const ENHANCEMENT_FORCE_STOPPED_KEY = 'autoEnhancementForceStopped'; // Permanent stop flag

// PERMANENT DISABLE: Enhancement system stopped - using existing 2,292+ enhanced modules
localStorage.setItem(ENHANCEMENT_FORCE_STOPPED_KEY, 'true');
localStorage.setItem(ENHANCEMENT_DISABLED_KEY, 'true');

interface EnhancementState {
  isRunning: boolean;
  progress: EnhancementProgress | null;
  hasRun: boolean;
  lastRun: Date | null;
  error: string | null;
}

export const useAutoContentEnhancement = () => {
  const [state, setState] = useState<EnhancementState>({
    isRunning: false,
    progress: null,
    hasRun: false,
    lastRun: null,
    error: null
  });

  /**
   * Check if enhancement is needed or if failed operations need restart
   * IMPROVED: Better health checks and failure detection
   */
  const checkEnhancementNeeded = useCallback(async (): Promise<boolean> => {
    try {
      // Check if disabled by user or force stopped
      const isDisabled = localStorage.getItem(ENHANCEMENT_DISABLED_KEY);
      const isForceStopped = localStorage.getItem(ENHANCEMENT_FORCE_STOPPED_KEY);
      
      if (isDisabled === 'true' || isForceStopped === 'true') {
        logger.info('✓ Enhancement disabled by user, skipping');
        return false;
      }

      // Check if there's already an active operation
      const { data: activeOps } = await supabase
        .from('content_enhancement_operations')
        .select('id, status, created_at, processed_items, total_items, failed_items')
        .eq('status', 'in_progress')
        .order('created_at', { ascending: false })
        .limit(1);

      if (activeOps && activeOps.length > 0) {
        const activeOp = activeOps[0];
        
        // Check if operation is healthy or stale
        const operationAge = Date.now() - new Date(activeOp.created_at).getTime();
        const sixHours = 6 * 60 * 60 * 1000;
        
        if (operationAge > sixHours) {
          logger.warn(`⚠️ Active operation is >6 hours old - marking as failed`);
          await supabase
            .from('content_enhancement_operations')
            .update({
              status: 'failed',
              completed_at: new Date().toISOString(),
              error_message: 'Operation timeout - exceeded 6 hours'
            })
            .eq('id', activeOp.id);
          
          // Will check for restart on next iteration
          return false;
        }
        
        logger.info('⚠️ Enhancement already running, resuming...');
        setState(prev => ({ ...prev, isRunning: true }));
        return true;
      }

      // Check for failed operations that can be restarted
      const { data: failedOps } = await supabase
        .from('content_enhancement_operations')
        .select('id, status, processed_items, total_items, failed_items, created_at')
        .eq('status', 'failed')
        .order('started_at', { ascending: false })
        .limit(1);

      if (failedOps && failedOps.length > 0) {
        const failedOp = failedOps[0];
        
        // Calculate failure rate
        const failureRate = failedOp.total_items > 0 
          ? failedOp.failed_items / failedOp.total_items 
          : 0;
        
        // Don't restart if failure rate >50%
        if (failureRate > 0.5) {
          logger.warn(`⚠️ Not restarting operation with ${Math.round(failureRate * 100)}% failure rate`);
          return false;
        }
        
        // Check if there are pending modules
        const { count: pendingCount } = await supabase
          .from('module_processing_status')
          .select('*', { count: 'exact', head: true })
          .eq('operation_id', failedOp.id)
          .eq('status', 'pending');

        if (pendingCount && pendingCount > 0) {
          logger.info(`🔄 Found failed operation with ${pendingCount} pending modules - will auto-restart`);
          return true;
        }
      }

      // Check if there are modules to enhance
      const service = new AutoContentEnhancementService();
      const isNeeded = await service.isMigrationNeeded();
      
      return isNeeded;
    } catch (error) {
      logger.error('Failed to check enhancement status:', error);
      return false;
    }
  }, []);

  /**
   * Run automatic enhancement
   */
  const runAutoEnhancement = useCallback(async () => {
    if (state.isRunning) {
      logger.warn('⚠️ Enhancement already running');
      return;
    }

    setState(prev => ({ ...prev, isRunning: true, error: null }));

    try {
      logger.info('🚀 Starting automatic content enhancement...');

      const service = new AutoContentEnhancementService();
      
      await service.enhanceAllContent((progress) => {
        setState(prev => ({ ...prev, progress }));
        
        // Log progress updates
        if (progress.currentModule) {
          logger.info(`📝 Processing: ${progress.currentModule} (${progress.processedModules}/${progress.totalModules})`);
        }
      });

      // Mark as permanently completed
      localStorage.setItem(ENHANCEMENT_COMPLETED_KEY, 'true');
      localStorage.setItem(LAST_ENHANCEMENT_KEY, new Date().toISOString());
      
      setState(prev => ({
        ...prev,
        isRunning: false,
        hasRun: true,
        lastRun: new Date()
      }));

      logger.info('✅ Automatic enhancement completed successfully and marked as permanent');
    } catch (error) {
      logger.error('❌ Auto-enhancement failed:', error);
      setState(prev => ({
        ...prev,
        isRunning: false,
        error: error instanceof Error ? error.message : 'Unknown error'
      }));
    }
  }, [state.isRunning]);

  /**
   * Trigger enhancement if needed (smart check)
   */
  const triggerEnhancementIfNeeded = useCallback(async () => {
    const isNeeded = await checkEnhancementNeeded();
    
    if (isNeeded) {
      logger.info('✓ Enhancement needed, starting background process...');
      await runAutoEnhancement();
    } else {
      logger.info('✓ No enhancement needed at this time');
    }
  }, [checkEnhancementNeeded, runAutoEnhancement]);

  /**
   * Disable automatic enhancement
   */
  const disableAutoEnhancement = useCallback(() => {
    localStorage.setItem(ENHANCEMENT_DISABLED_KEY, 'true');
    logger.info('🚫 Auto-enhancement disabled');
  }, []);

  /**
   * Enable automatic enhancement
   */
  const enableAutoEnhancement = useCallback(() => {
    localStorage.removeItem(ENHANCEMENT_DISABLED_KEY);
    logger.info('✅ Auto-enhancement enabled');
  }, []);

  /**
   * Force stop all enhancement permanently
   */
  const forceStopEnhancement = useCallback(async () => {
    localStorage.setItem(ENHANCEMENT_FORCE_STOPPED_KEY, 'true');
    localStorage.setItem(ENHANCEMENT_COMPLETED_KEY, 'true');
    
    // Call edge function to cancel all in-progress operations
    try {
      const { data, error } = await supabase.functions.invoke('stop-enhancement');
      if (error) throw error;
      logger.info('🛑 Force stopped all enhancement operations:', data);
    } catch (error) {
      logger.error('Failed to call stop-enhancement function:', error);
    }
    
    setState({
      isRunning: false,
      progress: null,
      hasRun: false,
      lastRun: null,
      error: null
    });
    
    logger.info('🛑 Enhancement permanently stopped');
  }, []);

  /**
   * Reset permanent completion flag (allows re-running enhancement)
   */
  const resetPermanentCompletion = useCallback(() => {
    localStorage.removeItem(ENHANCEMENT_COMPLETED_KEY);
    localStorage.removeItem(ENHANCEMENT_FORCE_STOPPED_KEY);
    logger.info('🔄 Permanent completion flag reset - enhancement can run again');
  }, []);

  /**
   * Reset enhancement state and clean up invalid operations
   */
  const resetEnhancementState = useCallback(async () => {
    // Clean up client state
    localStorage.removeItem(LAST_ENHANCEMENT_KEY);
    setState({
      isRunning: false,
      progress: null,
      hasRun: false,
      lastRun: null,
      error: null
    });
    
    // Clean up invalid database operations (0 progress completed operations)
    try {
      const { error } = await supabase
        .from('content_enhancement_operations')
        .delete()
        .eq('status', 'completed')
        .eq('processed_items', 0)
        .eq('successful_items', 0);
      
      if (error) {
        logger.error('Failed to clean up invalid operations:', error);
      } else {
        logger.info('🗑️ Cleaned up invalid operations from database');
      }
    } catch (error) {
      logger.error('Error cleaning up operations:', error);
    }
    
    logger.info('🔄 Enhancement state reset');
  }, []);


  return {
    ...state,
    checkEnhancementNeeded,
    runAutoEnhancement,
    triggerEnhancementIfNeeded,
    disableAutoEnhancement,
    enableAutoEnhancement,
    resetEnhancementState,
    resetPermanentCompletion,
    forceStopEnhancement
  };
};
